import { Check } from '@phosphor-icons/react';
import { cn } from '@reelplicate/ui';
import { Fragment } from 'react';

import { ONBOARDING_STEPS } from './steps';

type StepState = 'complete' | 'current' | 'upcoming';

/**
 * Figma Step (101:63), Horizontal: a 28px circle and a label, 10px apart.
 * Complete: brand/strong fill with a check, Body/Medium Strong text/primary.
 * Current: 2px brand/strong ring with a 4px brand/subtle-hover halo, the
 * number in text/brand, Label/Medium. Upcoming: 1.5px border/strong, the
 * number in text/tertiary, Body/Medium Strong text/secondary. Built here
 * until the library ships a Stepper.
 */
function Step({
  number,
  label,
  state,
}: {
  number: number;
  label: string;
  state: StepState;
}) {
  return (
    <li
      aria-current={state === 'current' ? 'step' : undefined}
      className="flex items-center gap-2.5"
    >
      <span
        className={cn(
          'text-label-sm flex size-7 shrink-0 items-center justify-center rounded-full',
          state === 'current' &&
            'bg-bg-surface border-brand-strong text-text-brand border-2 shadow-[0_0_0_4px_var(--color-brand-subtle-hover)]',
          state === 'upcoming' &&
            'bg-bg-surface border-border-strong text-text-tertiary border-[1.5px]',
          state === 'complete' && 'bg-brand-strong text-icon-inverse',
        )}
      >
        {state === 'complete' ? <Check aria-hidden className="size-3.5" /> : number}
      </span>
      <span
        className={cn(
          'hidden desktop:inline',
          state === 'current' && 'text-label-md',
          state === 'complete' && 'text-body-md-strong',
          state === 'upcoming' && 'text-body-md-strong text-text-secondary',
        )}
      >
        {label}
        {state === 'complete' && <span className="sr-only"> (done)</span>}
      </span>
    </li>
  );
}

/**
 * Figma "Stepper" in Onboarding / Top bar (125:482): the six stages with
 * 28 × 2 connectors, 12px apart - brand/strong up to the current step,
 * border/default after it. `current` past the last stage marks all complete.
 * Labels show from desktop up; narrower screens keep the circles.
 */
export function OnboardingStepper({ current }: { current: number }) {
  return (
    <ol aria-label="Onboarding progress" className="flex items-center gap-3">
      {ONBOARDING_STEPS.map(({ label }, i) => {
        const number = i + 1;
        const state: StepState =
          number < current ? 'complete' : number === current ? 'current' : 'upcoming';
        return (
          <Fragment key={label}>
            {i > 0 && (
              <li
                aria-hidden
                className={cn(
                  'h-0.5 w-7 shrink-0 rounded-[1px]',
                  number <= current ? 'bg-brand-strong' : 'bg-border-default',
                )}
              />
            )}
            <Step number={number} label={label} state={state} />
          </Fragment>
        );
      })}
    </ol>
  );
}
